export type ReleaseBucket = {
  key: "updates" | "fixes" | "security" | "removed"
  title: string
  description: string
  items: string[]
  accentColor: string
}

export type ReleaseNote = {
  releaseLabel: string
  releaseDate: string
  summary: string
  buckets: ReleaseBucket[]
}

// releaseNotesHistory stores the in-app implementation history shown to operators.
export const releaseNotesHistory: ReleaseNote[] = [
  {
    releaseLabel: "Czytelny brak materiału w meldunku",
    releaseDate: "2026-09-21",
    summary:
      "Błąd braku materiału podczas tworzenia meldunku pokazuje teraz konkretny produkt i podpowiada, jak uzupełnić wybraną partię lub lokalizację.",
    buckets: [
      {
        key: "fixes",
        title: "Łatwiejsza korekta ilości materiału",
        description:
          "Komunikat rozróżnia ilość wymaganą przez technologię, ilość pokrytą wybranymi partiami lub lokalizacjami oraz brakującą ilość.",
        accentColor: "red.500",
        items: [
          "Operator widzi SKU i nazwę produktu zamiast technicznego identyfikatora.",
          "Komunikat wskazuje, ile materiału trzeba dodać z kolejnej partii lub lokalizacji.",
          "Przy częściowej serii meldunków zachowana jest informacja o utworzonych meldunkach i przyczynie zatrzymania.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Waga jednostkowa produktu",
    releaseDate: "2026-09-19",
    summary:
      "Do produktu można teraz opcjonalnie zapisać wagę jednej jednostki, np. sztuki, kartonu lub palety.",
    buckets: [
      {
        key: "updates",
        title: "Waga w kartotece produktu",
        description:
          "Waga jest przechowywana w kilogramach i pozostaje oddzielona od masy materiałów opakowaniowych raportowanych dla PPWR.",
        accentColor: "blue.500",
        items: [
          "Pole Waga jednej jednostki [kg] jest opcjonalne i dostępne przy tworzeniu oraz edycji produktu.",
          "Można zapisać wagę produktu, kartonu lub palety, jeśli taka pozycja ma własne SKU i jednostkę miary.",
          "Wartość można również uzupełniać przez import CSV; pusta komórka nie usuwa wcześniej zapisanej wagi.",
        ],
      },
    ],
  },
  {
    releaseLabel:
      "Stockerra 2.1.9 — poprawne stany krytyczne produktów w partiach",
    releaseDate: "2026-09-19",
    summary:
      "Minimalny stan produktu jest teraz porównywany z jego łączną ilością w całym magazynie, niezależnie od podziału na partie i lokacje.",
    buckets: [
      {
        key: "fixes",
        title: "Wiarygodna kontrola stanów minimalnych",
        description:
          "Raporty i widok magazynu oceniają produkt jako całość, zachowując szczegóły poszczególnych partii i lokacji do pracy operacyjnej.",
        accentColor: "blue.500",
        items: [
          "Produkt nie jest już oznaczany jako krytyczny tylko dlatego, że jego zapas jest rozdzielony między kilka partii lub lokalizacji.",
          "Raport krytycznych stanów zawiera jedną pozycję produktu na magazyn oraz pokazuje łączny stan, minimum i dodatnią wartość deficytu.",
          "Widok magazynu, filtry statusu, podsumowanie i eksport CSV korzystają z tego samego statusu produktu wyliczonego przez serwer.",
          "Rezerwacje partii są uwzględniane jednokrotnie, a historyczne raporty zachowują dotychczasowy układ lokalizacji.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Prostszy łańcuch produkcji i wiele meldunków",
    releaseDate: "2026-09-19",
    summary:
      "Łańcuch produkcji pokazuje teraz tylko informacje potrzebne do pracy, a rejestracja zlecenia może obejmować kilka osobnych meldunków.",
    buckets: [
      {
        key: "updates",
        title: "Czytelniejsza praca z łańcuchem produkcji",
        description:
          "Usunięto pomocniczą animację podglądu, aby trójkąt przy zleceniu jednoznacznie oznaczał rzeczywistą rejestrację produkcji.",
        accentColor: "blue.500",
        items: [
          "Widok zachowuje karty zleceń, ostrzeżenia materiałowe, statusy, postęp, filtry, minimapę i eksport SVG.",
          "Z jednego zlecenia można utworzyć kilka osobnych meldunków; pełne realizacje są rejestrowane po jednej, a końcowa część ułamkowa jako meldunek częściowy.",
          "Jeśli część meldunków została już utworzona, system odświeża dane i blokuje przypadkowe ponowienie operacji do czasu weryfikacji.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Czytelna kontrola powtórzonych materiałów w technologii",
    releaseDate: "2026-09-19",
    summary:
      "Podczas tworzenia nowej wersji lub edycji technologii system od razu wskazuje materiał użyty więcej niż raz i pokazuje miejsca wymagające poprawy.",
    buckets: [
      {
        key: "fixes",
        title: "Zrozumiałe komunikaty o powtórzonych materiałach",
        description:
          "Ten sam produkt nadal może wystąpić tylko raz w wejściach technologii — jako materiał podstawowy albo jako zamiennik — ale konflikt można teraz łatwo odnaleźć i poprawić.",
        accentColor: "blue.500",
        items: [
          "Próba ponownego wybrania materiału jest od razu odrzucana, a komunikat wskazuje produkt oraz pozycję, w której został już użyty.",
          "Technologia skopiowana do nowej wersji pokazuje trwałe ostrzeżenie ze wszystkimi konfliktowymi pozycjami i pozostaje zablokowana do zapisu do czasu usunięcia powtórzenia.",
          "Komunikaty walidacyjne są prezentowane po polsku i nie pokazują operatorowi technicznych identyfikatorów ani surowych komunikatów systemowych.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Domyślna ilość jednej sztuki w dokumentach magazynowych",
    releaseDate: "2026-08-26",
    summary:
      "Nowa pozycja dokumentu MMW, WZ, ZS lub OS rozpoczyna się od jednej sztuki; dla MMW, WZ i ZS przy mniejszej dostępności używana jest bezpieczna ilość ułamkowa.",
    buckets: [
      {
        key: "updates",
        title: "Czytelniejsza ilość początkowa",
        description:
          "Dodanie produktu nie wstawia już automatycznie całego dostępnego stanu do nowej pozycji.",
        accentColor: "blue.500",
        items: [
          "Nowa pozycja MMW, WZ, ZS i OS otrzymuje domyślnie 1 sztukę i pozostaje dostępna do ręcznej edycji.",
          "Jeśli na wybranej lokalizacji MMW, WZ lub ZS dostępne jest mniej niż 1 sztuka, system proponuje dokładną dostępną ilość, aby nie tworzyć od razu pozycji przekraczającej stan; OS nadal zaczyna od 1 sztuki.",
          "Zmiana dotyczy wartości początkowej w edytorze; rezerwacje, zatwierdzanie i kontrola stanu magazynowego nadal są wykonywane przez serwer.",
        ],
      },
    ],
  },
  {
    releaseLabel:
      "Stockerra 2.1.7 — ostrzeżenia o brakach materiałowych w produkcji",
    releaseDate: "2026-08-23",
    summary:
      "Wersja 2.1.7 dodaje bieżący podgląd dostępności materiałów w łańcuchu produkcji i podczas tworzenia meldunku. Operator otrzymuje czytelny wskaźnik braków przed zatwierdzeniem operacji, a końcowa kontrola magazynowa nadal odbywa się bezpiecznie przy zapisie meldunku.",
    buckets: [
      {
        key: "updates",
        title: "Wczesne ostrzeganie o brakach materiałów",
        description:
          "Dostępność surowców jest sprawdzana przed rozpoczęciem kolejnej partii i podczas przygotowywania meldunku produkcyjnego.",
        accentColor: "orange.500",
        items: [
          "Łańcuch produkcji pokazuje liczbę zleceń, dla których brakuje materiałów do wykonania następnej partii, oraz oznacza takie zlecenia pomarańczową ikoną z wykrzyknikiem.",
          "Formularz Nowy meldunek automatycznie przelicza dostępność po zmianie liczby realizacji technologii, wybranego surowca lub zamiennika oraz ilości odpadu.",
          "W formularzu Nowy meldunek brakujący materiał jest oznaczony bezpośrednio przy właściwym produkcie i czerwonym wierszem.",
          "Liczbę realizacji technologii można zmieniać strzałkami co jedną sztukę; ręczne wartości ułamkowe nadal są obsługiwane.",
          "Czerwone oznaczenie brakującego materiału znika, gdy bieżąca konfiguracja meldunku ma wystarczającą ilość materiałów; podgląd nie blokuje samodzielnie zatwierdzenia operacji.",
        ],
      },
      {
        key: "fixes",
        title: "Wiarygodny i bezpieczny podgląd dostępności",
        description:
          "Ostrzeżenie uwzględnia rzeczywiste zapasy i rezerwacje, pozostając podglądem bez wpływu na stan magazynu.",
        accentColor: "blue.500",
        items: [
          "Sprawdzanie agreguje zapotrzebowanie na ten sam produkt, uwzględnia rezerwacje, usługi, zamienniki i ilości odpadu oraz stosuje zgodne zaokrąglenia ilości.",
          "Niepełna, sprzeczna lub niedostępna odpowiedź nie jest traktowana jako potwierdzenie dostępności — operator otrzymuje osobny komunikat o braku możliwości wykonania sprawdzenia.",
          "Podgląd nie tworzy rezerwacji, ruchów magazynowych ani meldunków; ostateczna walidacja wybranych lokalizacji i partii nadal odbywa się podczas zatwierdzania meldunku.",
          "Tymczasowe oznaczenia braków nie są umieszczane w eksportowanym diagramie SVG łańcucha produkcji.",
          "Długie nazwy produktów na połączeniach łańcucha są skracane do szerokości etykiety, a pełna nazwa pozostaje dostępna po wskazaniu kursorem.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Stockerra 2.1.6 — poprawne powiązanie faktur KSeF z PZ",
    releaseDate: "2026-08-16",
    summary:
      "Wersja 2.1.6 poprawia powiązanie faktury zakupowej z dokumentem PZ utworzonym na jej podstawie. Po zatwierdzeniu przyjęcia aktualny związek FZ–PZ oraz właściwy status są od razu widoczne w widokach KSeF.",
    buckets: [
      {
        key: "fixes",
        title: "Spójne powiązanie FZ z wygenerowanym PZ",
        description:
          "Zatwierdzenie PZ utworzonego z faktury KSeF aktualizuje dokumenty i widoki jako jedną spójną operację.",
        accentColor: "blue.500",
        items: [
          "Po zatwierdzeniu PZ wygenerowanego z faktury KSeF system automatycznie tworzy końcowe powiązanie FZ–PZ i oznacza fakturę właściwym statusem.",
          "Szczegóły faktury, skrzynka KSeF i rejestr FZ są odświeżane po zatwierdzeniu, dzięki czemu dokument nie pozostaje błędnie oznaczony jako „Nowa z KSeF” ani „PZ potwierdzony — do powiązania”.",
          "Ponowienie zatwierdzenia nie tworzy podwójnego powiązania ani dodatkowych ruchów i stanów magazynowych.",
          "Dla wcześniej zatwierdzonego PZ dostępna jest kontrolowana akcja naprawy powiązania z poziomu szczegółów faktury KSeF, zgodnie z uprawnieniami operatora.",
        ],
      },
    ],
  },
  {
    releaseLabel:
      "Stockerra 2.1.5 — zamienniki surowców, karta specyfikacji partii i numer BDO",
    releaseDate: "2026-08-07",
    summary:
      "Wersja 2.1.5 wprowadza obsługę zamienników surowców w technologiach i meldunkach produkcyjnych, generowanie dwujęzycznych kart specyfikacji partii (PDF), a także obsługę numeru BDO w profilu firmy, kartotece kontrahentów i wydrukach dokumentów.",
    buckets: [
      {
        key: "updates",
        title: "Zamienniki surowców w technologii i produkcji",
        description:
          "Elastyczne definiowanie i rozliczanie alternatywnych surowców dla pozycji technologicznych.",
        accentColor: "green.500",
        items: [
          "W definicji technologii produkcyjnej dodano możliwość określania zamienników (substytutów) dla każdego materiału wejściowego z własnym priorytetem oraz przelicznikiem ilościowym (ratio).",
          "Podczas rejestracji meldunku produkcyjnego operator może zastąpić surowiec główny zdefiniowanym zamiennikiem, zachowując automatyczne przeliczanie wymaganej ilości wedle ustalonego przelicznika.",
          "Rozliczanie zamienników w meldunku produkcyjnym wspiera wybór konkretnych partii oraz magazynowych lokalizacji pobrania.",
          "Na liście pozycji technologii oraz na ekranach zleceń produkcyjnych dodano podgląd zdefiniowanych zamienników i priorytetów użycia.",
        ],
      },
      {
        key: "updates",
        title: "Karta Specyfikacji Partii (Meldunek PDF)",
        description:
          "Oficjalny arkusz specyfikacji partii produkcyjnej z zestawieniem surowców i kontrolą wykonania.",
        accentColor: "teal.500",
        items: [
          "Wprowadzono generator dwujęzycznych (PL/EN) dokumentów PDF dla Karty Specyfikacji Partii Produkcyjnej (Meldunku).",
          "Wydruk specyfikacji zawiera dane wyprodukowanej partii, parametry zlecenia, zestawienie użytych surowców i zamienników, numery partii wejściowych, lokalizacje oraz dane osoby odpowiedzialnej.",
          "Karta specyfikacji jest dostępna do pobrania bezpośrednio z poziomu szczegółów zlecenia produkcyjnego oraz widoku meldunku.",
        ],
      },
      {
        key: "updates",
        title: "Obsługa numeru BDO i wydruki",
        description:
          "Pełna obsługa numeru BDO w profilu firmy, kartotece kontrahentów i dokumentach PDF.",
        accentColor: "purple.500",
        items: [
          "W profilu i ustawieniach firmy dodano pole numeru BDO (Baza danych o produktach i opakowaniach oraz o gospodarce odpadami).",
          "W kartotece kontrahentów dodano obsługę numeru BDO wraz z walidacją oraz synchronizacją podczas pobierania danych z GUS.",
          "Numer BDO firmy oraz kontrahenta jest automatycznie prezentowany w nagłówkach i sekcjach identyfikacyjnych na wydrukach PDF dokumentów magazynowych i handlowych.",
        ],
      },
      {
        key: "fixes",
        title: "Precyzja walidacji i stabilność meldunków",
        description:
          "Udoskonalenia w przeliczaniu ułamkowych ilości zamienników oraz stabilności rozliczeń FIFO.",
        accentColor: "blue.500",
        items: [
          "Poprawiono walidację ilości oraz zaokrąglenia dla ułamkowych i całkowitych pobrań surowców zamiennych w meldunkach produkcyjnych.",
          "Zapewniono poprawność rozliczania warstw kosztowych FIFO i partii magazynowych przy mieszanym zużyciu surowców głównych i zamienników.",
          "Usprawniono odświeżanie danych i obsługę błędów walidacyjnych w widokach edycji technologii oraz zlecenia produkcyjnego.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Stockerra 2.1.4 — biała lista VAT kontrahentów",
    releaseDate: "2026-08-02",
    summary:
      "Wersja 2.1.4 umożliwia pobieranie rachunków kontrahenta z oficjalnej białej listy VAT, sprawdzanie zapisanych rachunków dla wybranego dnia oraz zachowanie danych potwierdzających wynik weryfikacji.",
    buckets: [
      {
        key: "updates",
        title: "Rachunki z białej listy VAT",
        description:
          "Dane rachunków można pobrać i zweryfikować bez opuszczania kartoteki kontrahenta.",
        accentColor: "green.500",
        items: [
          "W zakładce Finanse dodano wyszukiwanie rachunków podmiotu na podstawie polskiego numeru NIP; operator wybiera, które z odnalezionych rachunków dodać do kartoteki.",
          "Zapisany rachunek można sprawdzić na białej liście VAT dla wskazanej daty, otrzymując czytelny wynik „Na białej liście” albo „Brak na białej liście”.",
          "Przy wyniku widoczne są data zapytania, zweryfikowany NIP, czas sprawdzenia i identyfikator zapytania Ministerstwa Finansów.",
          "Dodano historię wcześniejszych sprawdzeń rachunku, dostępną również użytkownikom posiadającym uprawnienie tylko do odczytu kontrahentów.",
        ],
      },
      {
        key: "fixes",
        title: "Wiarygodne dane i bezpieczna obsługa zmian",
        description:
          "Status rachunku pozostaje powiązany z dokładnie tym NIP-em, numerem konta i dniem, dla których wykonano sprawdzenie.",
        accentColor: "blue.500",
        items: [
          "Zmiana NIP-u lub rzeczywistego numeru rachunku natychmiast usuwa nieaktualny status z formularza, natomiast samo formatowanie IBAN-u nie unieważnia wyniku.",
          "Równoważne zapisy polskiego rachunku — IBAN z prefiksem PL oraz 26-cyfrowy NRB — są traktowane jako ten sam rachunek i nie mogą zostać zapisane jako duplikaty.",
          "Wynik weryfikacji jest zapisywany przez serwer wraz z danymi potwierdzającymi i nie może zostać ustawiony ręcznie z poziomu formularza.",
          "Dodano czytelne komunikaty dla niepoprawnych danych, niezapisanego rachunku, wyczerpanego limitu zapytań oraz czasowej niedostępności usługi Ministerstwa Finansów.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Stockerra 2.1.3 — wizualizacja technologii i produkcji",
    releaseDate: "2026-07-30",
    summary:
      "Wersja 2.1.3 wprowadza czytelne mapy technologii i zależności między zleceniami produkcyjnymi, edytowalne przepływy technologiczne oraz prostszą nawigację po obszarze Produkcja.",
    buckets: [
      {
        key: "updates",
        title: "Drzewo technologii i łańcuch produkcji",
        description:
          "Nowe widoki graficzne pomagają szybciej prześledzić sposób wytwarzania produktu i zależności między zleceniami.",
        accentColor: "green.500",
        items: [
          "Drzewo technologii pokazuje wielopoziomowe powiązania produktów, surowców i technologii; można wybrać korzeń, przesuwać i przybliżać diagram oraz przechodzić do powiązanych kartotek.",
          "Łańcuch produkcji prezentuje zlecenia jako połączony diagram ze statusem, postępem wykonania i produktem przekazywanym do kolejnego etapu.",
          "Po wybraniu produktu widok pokazuje dostępny łańcuch zleceń prowadzących do jego wytworzenia, bez niezależnych zleceń i dalszych odbiorców produktu.",
          "Filtry statusu, produktu i dat ułatwiają zawężenie diagramu; zakres dat uwzględnia nakładające się plany, a dla zleceń bez planu wykorzystuje datę utworzenia.",
        ],
      },
      {
        key: "updates",
        title: "Edytowalne przepływy technologiczne",
        description:
          "Powtarzalne procesy można zapisać jako własne diagramy i rozwijać je bez utraty wybranego układu.",
        accentColor: "teal.500",
        items: [
          "Dodano listę zapisanych przepływów z możliwością tworzenia, wyszukiwania, otwierania, zmiany nazwy i usuwania diagramów.",
          "W edytorze można dodawać wybrane wersje technologii, swobodnie rozmieszczać bloki, łączyć zgodne etapy, usuwać połączenia lub technologie oraz korzystać z automatycznego układu.",
          "Zmiany są zapisywane automatycznie, a widoczny status informuje o oczekiwaniu, zapisie, błędzie lub konflikcie; przerwana praca może zostać wznowiona albo zapisana jako kopia.",
          "Przepływy są definicjami pomocniczymi — ich edycja nie tworzy zleceń produkcyjnych i nie zmienia stanów magazynowych.",
        ],
      },
      {
        key: "fixes",
        title: "Czytelniejsza nawigacja i stabilizacja widoków",
        description:
          "Porządki w obszarze Produkcja skracają drogę do narzędzi i pozostawiają więcej miejsca na diagramy.",
        accentColor: "blue.500",
        items: [
          "Rozbudowane menu Produkcja podzielono na trzy przejrzyste wejścia: Technologie, Zlecenia produkcyjne i Automatyzacja; każda strona prowadzi dalej przez czytelne karty.",
          "Pusty widok drzewa otrzymał instrukcję rozpoczęcia i wyszukiwarkę produktu lub technologii, dzięki czemu od razu wiadomo, jak zbudować diagram.",
          "Po wybraniu korzenia drzewo przechodzi do kompaktowego układu, a edytor przepływu i łańcuch produkcji lepiej wykorzystują dostępną wysokość ekranu.",
          "Uspójniono zachowanie filtrów, pustych wyników, wyboru produktu i konfliktów zapisu, aby przyczyna braku danych lub zatrzymania zapisu była widoczna dla operatora.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Stockerra 2.1.2 — KSeF i powiadomienia na żywo",
    releaseDate: "2026-07-30",
    summary:
      "Wersja 2.1.2 porządkuje codzienną pracę z KSeF, pozwala tworzyć edytowalne wersje robocze PZ z odebranych faktur oraz wprowadza powiadomienia systemowe na żywo w aplikacji internetowej i mobilnej.",
    buckets: [
      {
        key: "updates",
        title: "Szybszy dostęp do KSeF",
        description:
          "Najważniejsze operacje na fakturach KSeF są teraz dostępne bezpośrednio z osobnej sekcji menu.",
        accentColor: "green.500",
        items: [
          "Dodano osobną sekcję KSeF w menu bocznym z bezpośrednim dostępem do odebranych faktur oraz dokumentów FZ powiązanych z PZ.",
          "Rejestr powiązanych FZ otrzymał własny widok z wyszukiwaniem i filtrowaniem okresu, a pozycja FZ nie jest już dublowana w ogólnym rejestrze Dokumenty.",
          "Konfiguracja KSeF pozostała w sekcji Firma, dzięki czemu ustawienia administracyjne są oddzielone od codziennej pracy z fakturami.",
        ],
      },
      {
        key: "updates",
        title: "Tworzenie PZ z faktury KSeF",
        description:
          "Odebrana faktura może być podstawą kontrolowanej, edytowalnej wersji roboczej przyjęcia.",
        accentColor: "teal.500",
        items: [
          "Z poziomu faktury KSeF można utworzyć niezatwierdzoną wersję roboczą PZ z zachowaniem powiązania z dokumentem źródłowym.",
          "Przed utworzeniem PZ operator może sprawdzić kontrahenta i produkty, poprawić dopasowania, rozdzielić jedną pozycję faktury na kilka pozycji przyjęcia oraz wskazać właściwe lokalizacje.",
          "Utworzony dokument pozostaje dostępny do dalszej edycji w standardowym widoku PZ.",
        ],
      },
      {
        key: "updates",
        title: "Powiadomienia systemowe na żywo",
        description:
          "Ważne zdarzenia docierają do aplikacji internetowej i mobilnej bez okresowego odświeżania.",
        accentColor: "purple.500",
        items: [
          "Web i aplikacja mobilna odbierają obsługiwane powiadomienia przez stałe połączenie SSE, a po ponownym połączeniu automatycznie uzgadniają aktualną listę wiadomości.",
          "Powiadomienia o dokumentach ZAP, ZZ, ZM, PZ i WZ są tworzone przy ich pierwszym zatwierdzeniu; jeden kanał obejmuje również alerty stanów krytycznych, terminów ważności i automatycznego uzupełniania.",
          "Użytkownik może włączyć lub wyłączyć wybrane kategorie powiadomień, a lista respektuje jego uprawnienia oraz zachowuje stan przeczytania i odrzucenia.",
          "W aplikacji mobilnej nowe wiadomości mogą wyświetlać lokalny alert, gdy aplikacja jest uruchomiona.",
        ],
      },
      {
        key: "fixes",
        title: "Stabilność i bezpieczna kontrola",
        description:
          "Nowe przepływy zachowują kontrolę operatora i bezpiecznie odtwarzają aktualny stan po przerwie połączenia.",
        accentColor: "blue.500",
        items: [
          "Samo wygenerowanie wersji roboczej nie zmienia stanów magazynowych — przyjęcie następuje dopiero po standardowym zatwierdzeniu PZ.",
          "System blokuje zatwierdzenie pozycji wymagających lokalizacji, dopóki brakujące lokalizacje nie zostaną uzupełnione.",
          "Uspójniono komunikaty walidacyjne i bezpieczną obsługę błędów zapisu, aby operator otrzymywał użyteczną informację bez ujawniania szczegółów technicznych.",
          "Po przerwie połączenia lista powiadomień jest uzupełniana z serwera i chroniona przed duplikatami, a historyczne zdarzenia nie są ponownie prezentowane jako nowe alerty.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Stockerra 2.1.1 — nowy standard pracy z dokumentami WMS",
    releaseDate: "2026-07-19",
    summary:
      "Wersja 2.1.1 wprowadza spójniejszy i wygodniejszy interfejs dokumentów magazynowych, rozszerza obsługę partii i raportów oraz wzmacnia stabilność, kontrolę dostępu i bezpieczeństwo operacji na stanach.",
    buckets: [
      {
        key: "updates",
        title: "Spójniejszy interfejs dokumentów",
        description:
          "Ujednolicony sposób tworzenia i edycji dokumentów w codziennej pracy magazynu.",
        accentColor: "green.500",
        items: [
          "Przebudowano ekrany dokumentów PZ, WZ, PW, RW, MMW, ZZ, ZAP, OS, ZS i ZM, ujednolicając nagłówki, sekcje pozycji, akcje, załączniki oraz komunikaty potwierdzające.",
          "Nowe dokumenty są inicjalizowane jako wersje robocze i zapisywane automatycznie; operator widzi stan synchronizacji, a próba opuszczenia strony z niezapisanymi zmianami wymaga potwierdzenia.",
          "Wprowadzono wspólny selektor produktów z wyszukiwaniem, wyborem wielu pozycji, obsługą skanera kodów oraz czytelną prezentacją magazynu, lokalizacji, dostępności i partii.",
          "Usprawniono tabele dokumentów: dodano spójne numerowanie wierszy, lepsze szerokości kolumn, podpowiedzi dla długich nazw, wyróżnianie błędnych pól i bardziej przewidywalne przewijanie.",
          "Ujednolicono obsługę cen netto, VAT, wartości brutto i cen minimalnych, dzięki czemu błędy są wskazywane bezpośrednio przy edytowanej pozycji.",
        ],
      },
      {
        key: "updates",
        title: "Partie, raporty i wygodniejsza obsługa magazynu",
        description:
          "Nowe informacje i narzędzia wspierające identyfikowalność towaru oraz szybsze decyzje operatorów.",
        accentColor: "teal.500",
        items: [
          "Rozszerzono obsługę partii magazynowych o datę produkcji i termin ważności oraz udostępniono wybór konkretnej partii w dokumentach przyjęć, wydań, sprzedaży, przesunięć i produkcji.",
          "Dodano raporty krytycznych stanów i terminów ważności z historią raportów, konfiguracją harmonogramu, zakresem magazynów oraz wysyłką do użytkowników, ról i wskazanych adresów e-mail.",
          "Usprawniono zadania inwentaryzacyjne: lepiej prezentowane są partie i numery seryjne, filtry lokalizacji oraz status przebiegu liczenia i weryfikacji.",
          "Rozbudowano kartę produktu, analitykę cen, zarządzanie stanem minimalnym i widoczność lokalizacji, a na listach produktów dodano czytelniejszą prezentację kodów SKU i EAN.",
        ],
      },
      {
        key: "fixes",
        title: "Migracja i stabilność danych",
        description:
          "Aktualizacja porządkuje historyczne dane i wzmacnia niezawodność kluczowych procesów WMS.",
        accentColor: "blue.500",
        items: [
          "Migracja uruchamia się automatycznie przy starcie systemu: dostosowuje model partii i lokalizacji oraz odtwarza jednoznaczne powiązania historycznych stanów z warstwami kosztowymi FIFO.",
          "Niejednoznaczne dane historyczne nie są korygowane na podstawie przypuszczeń — system bezpiecznie blokuje ryzykowny przypadek i pozostawia ślad diagnostyczny do weryfikacji.",
          "Wzmocniono zgodność stanów, rezerwacji, partii i warstw kosztowych podczas PZ, WZ, MMP, MMR, RW, korekt inwentaryzacyjnych oraz raportowania produkcji.",
          "Dodano zabezpieczenia przed równoczesnym utworzeniem kilku aktywnych zadań inwentaryzacyjnych dla tego samego planu oraz poprawiono obsługę konfliktów i ponowień operacji.",
          "Poprawiono przypadki związane z ułamkowymi ilościami, pustą lokalizacją, przenoszeniem partii, anulowaniem i ponownym otwieraniem dokumentów oraz odbudową historycznych warstw FIFO.",
        ],
      },
      {
        key: "security",
        title: "Bezpieczeństwo i kontrola operacji",
        description:
          "Dodatkowe kontrole ograniczające nieautoryzowane lub niespójne operacje magazynowe.",
        accentColor: "purple.500",
        items: [
          "Rozszerzono kontrolę uprawnień i licencji na nowe operacje tworzenia wersji roboczych, usuwania dokumentów, generowania raportów oraz zarządzania załącznikami.",
          "Walidacje magazynu, ilości, cen, dostępności i partii są egzekwowane również po stronie serwera, niezależnie od kontroli widocznych w interfejsie.",
          "Wirtualny magazyn BRAK został wyłączony z operacyjnych przepływów, w których nie może reprezentować rzeczywistego miejsca składowania.",
          "Krytyczne migracje sprawdzają konflikty i nie uruchamiają systemu z wykrytą niespójnością, która mogłaby naruszyć tożsamość stanu lub blokady inwentaryzacyjne.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Wydruk PDF dla Korekt Inwentaryzacyjnych",
    releaseDate: "2026-06-24",
    summary:
      "Dodano pełną obsługę generowania i pobierania dwujęzycznych (PL/EN) dokumentów PDF dla Korekt Inwentaryzacyjnych (KI).",
    buckets: [
      {
        key: "updates",
        title: "Wydruk dokumentów KI",
        description:
          "Nowa funkcja umożliwiająca drukowanie i archiwizację wyników inwentaryzacji.",
        accentColor: "green.500",
        items: [
          "Wprowadzono generator dokumentów PDF dla Korekt Inwentaryzacyjnych (KI), prezentujący zestawienie towarów ze stanem systemowym, policzonym i odchyleniem.",
          "Wydruk jest zoptymalizowany pod format A4 z precyzyjnie dobranymi szerokościami kolumn dla maksymalnej czytelności.",
          "Dodano informację o osobie wystawiającej na dokumencie w celu ułatwienia audytu procesów inwentaryzacyjnych.",
          "Zaimplementowano pełne wsparcie dla druku dwujęzycznego (PL / EN), dostępnego bezpośrednio z poziomu paska akcji w szczegółach dokumentu.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Usługi i wybiórcza aktualizacja cen",
    releaseDate: "2026-06-24",
    summary:
      "Wprowadzono obsługę usług jako pozycji nie wymagających stanu magazynowego oraz elastyczniejsze zarządzanie aktualizacją cen przy PZ i PW.",
    buckets: [
      {
        key: "updates",
        title: "Usługi i stany magazynowe",
        description:
          "Nowe możliwości w zakresie zarządzania asortymentem i lokalizacjami.",
        accentColor: "green.500",
        items: [
          "Wprowadzono obsługę produktów oznaczonych jako usługi, które nie podlegają weryfikacji stanów i mogą być swobodnie dodawane do wydań (WZ) i przyjęć.",
          "Zapytania o stany magazynowe uwzględniają teraz również produkty o zerowym stanie, jeżeli posiadają historię przypisania do lokalizacji.",
          "Zaimplementowano naturalne (alfanumeryczne) sortowanie kodów lokalizacji podczas zadań inwentaryzacyjnych, co ułatwia planowanie trasy przejścia.",
        ],
      },
      {
        key: "updates",
        title: "Aktualizacje cen zakupu",
        description:
          "Pełniejsza kontrola nad zmianą cen przy zatwierdzaniu dokumentów wejściowych.",
        accentColor: "teal.500",
        items: [
          "Przy potwierdzaniu przyjęć (PZ) i przychodów wewnętrznych (PW) dodano możliwość aktualizacji cen zakupu tylko dla wybranych, zaznaczonych przez operatora produktów.",
          "Dodano nową opcję 'Potwierdź bez aktualizacji', która pozwala zatwierdzić dokument z rozbieżnymi cenami bez nadpisywania cen katalogowych w kartotece.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Dashboard operacyjny WMS",
    releaseDate: "2026-06-22",
    summary:
      "Dodano nowy dashboard operacyjny z podsumowaniem zakupów, sprzedaży, realizacji zamówień i produkcji dla bieżącego miesiąca, kwartału lub roku.",
    buckets: [
      {
        key: "updates",
        title: "Nowy dashboard",
        description:
          "Jedno miejsce do szybkiego przeglądu kluczowych wskaźników pracy magazynu.",
        accentColor: "green.500",
        items: [
          "Dodano nową stronę Dashboard w module WMS oraz ustawiono ją wysoko w menu bocznym, aby była łatwo dostępna po wejściu do systemu.",
          "Dashboard pokazuje dane dla trzech zakresów czasu: bieżący miesiąc, bieżący kwartał oraz bieżący rok liczony od 1 stycznia do dnia dzisiejszego.",
          "W sekcji zakupów prezentowane są potwierdzone dokumenty PZ: liczba dokumentów, łączna ilość i wartość netto.",
          "W sekcji sprzedaży prezentowane są potwierdzone dokumenty WZ: liczba dokumentów, łączna ilość i wartość netto.",
        ],
      },
      {
        key: "updates",
        title: "Realizacja dokumentów i produkcja",
        description:
          "Dodatkowe wskaźniki pomagające szybciej ocenić wykonanie procesów.",
        accentColor: "teal.500",
        items: [
          "Dodano wykres realizacji ZZ -> PZ z procentem zrealizowanej ilości oraz liczbą zamówień zakupowych.",
          "Dodano wykres realizacji OS -> WZ z procentem wydanej ilości oraz liczbą ofert sprzedaży powiązanych z wydaniami.",
          "Dodano blok produkcji z otwartymi zleceniami, ich postępem realizacji oraz podsumowaniem zamkniętej produkcji.",
          "Zamknięta produkcja aktualizuje teraz liczbę zamkniętych zleceń, wyprodukowaną ilość i wartość netto zgodnie z wybranym okresem.",
        ],
      },
      {
        key: "fixes",
        title: "Czytelność i stabilizacja",
        description:
          "Poprawki interfejsu wykonane przy wdrożeniu nowego dashboardu.",
        accentColor: "blue.500",
        items: [
          "Naprawiono przewijanie strony dashboardu, dzięki czemu wszystkie bloki można teraz wygodnie przeglądać w dół.",
          "Poprawiono działanie wykresów w ciemnym motywie: tooltipy i aktywne słupki są czytelne i nie znikają na ciemnym tle.",
          "Doprecyzowano semantykę sprzedaży w dashboardzie: wartość sprzedaży jest liczona wyłącznie z danych zapisanych na dokumentach WZ, bez mieszania z cenami z innych dokumentów.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Ceny przyjęć i pozostały stan FIFO",
    releaseDate: "2026-06-21",
    summary:
      "Rozszerzono obsługę cen zakupowych w dokumentach PZ i PW oraz dodano podgląd rzeczywistego pozostałego stanu według warstw FIFO na karcie produktu.",
    buckets: [
      {
        key: "updates",
        title: "Ceny PZ i PW",
        description:
          "Lepsza kontrola cen zakupu przy przyjmowaniu i produkcji wewnętrznej.",
        accentColor: "green.500",
        items: [
          "Dodano wykrywanie różnic między ceną z dokumentu PZ/PW a aktualną ceną zakupu produktu.",
          "Przy potwierdzaniu PZ i PW operator może zdecydować, czy zaktualizować aktualną cenę zakupu produktu na podstawie dokumentu.",
          "Pozycje PW przechowują teraz własną cenę netto, dzięki czemu koszt przyjęcia i warstwy FIFO nie zależą od późniejszych zmian ceny w kartotece produktu.",
          "Import PW z CSV obsługuje cenę pozycji, aby masowe przyjęcia wewnętrzne zachowywały prawidłową wycenę.",
        ],
      },
      {
        key: "updates",
        title: "Podgląd FIFO na produkcie",
        description:
          "Nowy widok pokazujący, ile sztuk zostało w otwartych warstwach kosztowych.",
        accentColor: "teal.500",
        items: [
          "Na karcie produktu dodano tabelę „Pozostały stan wg ceny FIFO” z magazynem, lokalizacją, źródłem, dokumentem, ceną netto, ilością i wartością netto.",
          "Tabela pokazuje pozostałe ilości według ceny przyjęcia lub produkcji, co ułatwia operatorowi zrozumienie, z których cen pochodzą aktualne zapasy.",
          "Dodano backendowy endpoint dla warstw FIFO produktu oraz testy agregacji ilości i wartości netto.",
        ],
      },
      {
        key: "fixes",
        title: "Spójność FIFO i stanów",
        description:
          "Poprawki zapewniające zgodność widoku FIFO z fizycznym stanem magazynu.",
        accentColor: "blue.500",
        items: [
          "Potwierdzony WZ zużywa teraz warstwy FIFO, a anulowanie WZ przywraca zużyte warstwy.",
          "Potwierdzone przesunięcia MMP przenoszą teraz warstwy FIFO do magazynu docelowego z zachowaniem pierwotnej daty FIFO i ceny warstwy, dzięki czemu przeniesienie nie odmładza towaru.",
          "Ujemne korekty inwentaryzacji zużywają FIFO zgodnie z kolejnością warstw, a dodatnie korekty tworzą nową warstwę typu inventory adjustment.",
          "Dodano migracje/backfill dla historycznych WZ, MMP i ujemnych korekt inwentaryzacji oraz rebalance WZ, które wcześniej mogły zużyć młodszą warstwę zamiast starszej warstwy przeniesionej MMP.",
          "Backfill pomija pojedyncze historyczne dokumenty bez możliwych do odtworzenia warstw FIFO i loguje je bez blokowania startu systemu.",
          "Dodano zabezpieczenie przed częściowym zużyciem FIFO, jeżeli dla dokumentu brakuje wystarczającej ilości w warstwach kosztowych.",
        ],
      },
      {
        key: "updates",
        title: "Karta produktu",
        description: "Czytelniejsza edycja produktu i lepszy układ informacji.",
        accentColor: "orange.500",
        items: [
          "Przebudowano ekran edycji produktu, aby ważne dane, statusy i akcje były czytelniejsze dla operatora.",
          "Uporządkowano sekcje formularza produktu: dane podstawowe, producent i dostawcy oraz ustawienia dodatkowe.",
          "Doprecyzowano komunikaty i układ przy zapisywaniu zmian produktu.",
        ],
      },
      {
        key: "fixes",
        title: "Stabilizacja interfejsu",
        description:
          "Drobne poprawki techniczne wykonane przy przygotowaniu gałęzi do merge.",
        accentColor: "purple.500",
        items: [
          "Poprawiono wybrane ostrzeżenia lint/build w komponentach frontendowych.",
          "Uspójniono obsługę wygenerowanego klienta API dla nowych pól i endpointów cenowych.",
          "Drobne poprawki w komponentach drukowania etykiet i układach formularzy zmniejszają ryzyko błędów po aktualizacji.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Ceny produktów i nowy druk etykiet ZPL",
    releaseDate: "2026-06-19",
    summary:
      "Rozszerzono obsługę cen i kosztów produktów oraz przebudowano druk etykiet pod pracę z lokalnym agentem Windows, instalatorem i testowym drukiem szablonów.",
    buckets: [
      {
        key: "updates",
        title: "Ceny i koszty produktów",
        description:
          "Nowe narzędzia do pracy z cenami sprzedaży, kosztami i historią zmian.",
        accentColor: "green.500",
        items: [
          "Dodano historię cen produktów oraz pola ceny netto, brutto, stawki VAT i ceny zakupu/kosztu na karcie produktu.",
          "Rozszerzono import CSV produktów o pola cenowe, dzięki czemu ceny można uzupełniać masowo razem z danymi kartotekowymi.",
          "Dodano aktualizację ceny zakupu/kosztu produktu na podstawie stanów magazynowych i warstw kosztowych.",
          "Usprawniono dokumenty sprzedaży i wydania, aby korzystały z nowych danych cenowych przy dodawaniu pozycji.",
        ],
      },
      {
        key: "updates",
        title: "Druk etykiet",
        description:
          "Pełniejszy przepływ pracy z etykietami ZPL i lokalnym drukowaniem.",
        accentColor: "orange.500",
        items: [
          "Dodano renderowanie etykiet w formacie ZPL z obsługą rozmiaru etykiety, DPI drukarki, kodów kreskowych i tekstu UTF-8.",
          "Dodano lokalny Stockerra Print Agent dla Windows, który udostępnia drukarki pod stałym adresem https://127.0.0.1:9101 i wysyła ZPL bezpośrednio do kolejki Windows.",
          "Dodano wersję agenta działającą w zasobniku systemowym Windows z autostartem po zalogowaniu użytkownika.",
          "Dodano instalator Windows z UI, który konfiguruje agenta, port RAW 9100, kolejkę drukarki, certyfikat lokalny i autostart.",
          "Dodano stronę „Agent drukowania” w menu etykiet z pobieraniem instalatora oraz pakietem ZIP do diagnostyki.",
          "Dodano testowy druk szablonu etykiety z poziomu edytora szablonów: operator może wydrukować lub pobrać testowy PDF/ZPL bez zapisywania zmian.",
        ],
      },
      {
        key: "fixes",
        title: "Poprawki i stabilizacja",
        description:
          "Usprawnienia usuwające problemy wykryte podczas wdrożenia druku etykiet.",
        accentColor: "blue.500",
        items: [
          "Ujednolicono komunikację frontend-agent do jednego portu HTTPS 9101, aby uniknąć rozbieżności między stanowiskami.",
          "Poprawiono obsługę błędów drukowania, tak aby system pokazywał czytelny komunikat zamiast surowej odpowiedzi HTML.",
          "Poprawiono skrypt instalacyjny Windows, aby działał stabilnie w Windows PowerShell niezależnie od kodowania znaków.",
          "Dodano instrukcje dla ostrzeżenia Windows SmartScreen / nieznanego wydawcy do czasu podpisania instalatora certyfikatem Code Signing.",
          "Usprawniono ZPL dla kodów kreskowych i parametrów drukarki, co ułatwia dopasowanie wydruku do rzeczywistej etykiety.",
        ],
      },
      {
        key: "security",
        title: "Bezpieczeństwo lokalnego druku",
        description:
          "Ograniczenia techniczne zmniejszające ryzyko ekspozycji agenta.",
        accentColor: "purple.500",
        items: [
          "Agent drukowania nasłuchuje lokalnie na 127.0.0.1 i nie powinien być wystawiany do sieci LAN ani internetu.",
          "Instalator przygotowuje lokalny certyfikat HTTPS, aby aplikacja WMS mogła komunikować się z agentem z poziomu przeglądarki.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Odblokowywanie przesunięć MMW i MMP",
    releaseDate: "2026-06-10",
    summary:
      "Wprowadzono funkcje bezpiecznego cofania zatwierdzonych dokumentów przesunięć (MMP) oraz wycofywania roboczych przesunięć (MMW) z pełnym odtworzeniem stanów magazynowych.",
    buckets: [
      {
        key: "updates",
        title: "Nowe funkcje",
        description:
          "Rozszerzone możliwości zarządzania ruchem międzymagazynowym.",
        accentColor: "green.500",
        items: [
          'Dodano funkcję "Odblokuj" dla zatwierdzonych dokumentów przyjęcia przesunięcia (MMP). Operacja ta cofa dokument do wersji roboczej, zdejmując towar ze stanu docelowego i przywracając rezerwacje na magazynie źródłowym.',
          "Dodano opcję odblokowywania i wycofywania przesunięć na poziomie dokumentu MMW, co automatycznie anuluje powiązany roboczy dokument MMP.",
          "System dba o spójność danych: odblokowanie MMP zostanie zablokowane, jeżeli ewentualny dokument niedoboru (MMR) z nim powiązany został już sfinalizowany.",
        ],
      },
      {
        key: "fixes",
        title: "Poprawki techniczne",
        description: "Zwiększenie stabilności systemu operacyjnego.",
        accentColor: "blue.500",
        items: [
          "Rozbudowano wewnętrzne mapowanie licencji (Capability Mapping) dla nowych ścieżek odblokowywania dokumentów.",
          "Ujednolicono przypisywanie uprawnień do wersji roboczych przy cofaniu dokumentów, eliminując błędy związane z relacyjnymi kluczami obcymi.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Zarządzanie dokumentami OS i ZS",
    releaseDate: "2026-06-10",
    summary:
      "Dodano możliwość odblokowywania i usuwania ofert sprzedaży (OS) oraz zamówień sprzedaży (ZS) z zachowaniem pełnej spójności magazynowej.",
    buckets: [
      {
        key: "updates",
        title: "Nowe funkcje",
        description:
          "Rozszerzone możliwości zarządzania dokumentami sprzedaży.",
        accentColor: "blue.500",
        items: [
          'Dodano funkcję "Odblokuj" dla potwierdzonych i anulowanych ofert (OS) i zamówień (ZS), przywracając je do statusu roboczego.',
          "Zablokowano możliwość odblokowania ZS, jeśli istnieje powiązany dokument wydania (WZ).",
          "Wprowadzono możliwość trwałego usuwania pustych dokumentów OS i ZS w statusie roboczym lub anulowanym.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Odblokowywanie potwierdzonych dokumentów PZ",
    releaseDate: "2026-06-09",
    summary:
      "Dodano możliwość bezpiecznego odblokowywania (przywracania do statusu roboczego) zatwierdzonych dokumentów PZ, bez konieczności ich całkowitego anulowania.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Dodano przycisk 'Odblokuj' dla potwierdzonych dokumentów przyjęcia (PZ). Zmienia on status dokumentu na roboczy (Draft) i precyzyjnie wycofuje dodany towar ze stanu magazynowego.",
          "Wprowadzono dedykowane uprawnienie systemowe 'wms.reception.reopen', które jest wymagane do wykonania odblokowania.",
          "Zabezpieczono stabilność magazynu: system blokuje odblokowanie PZ, jeśli jakikolwiek produkt z tego przyjęcia został już sprzedany, przesunięty lub zużyty na produkcji.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Zakresy inwentaryzacji i czytelniejsze blokady startu",
    releaseDate: "2026-06-09",
    summary:
      "Dopracowano planowanie inwentaryzacji od-do, podpisy pól w modalu tworzenia planu oraz modal blokady startu z pełniejszym opisem magazynu i blokującego zadania.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Plan inwentaryzacji pozwala teraz wskazać zakres lokacji od wybranego miejsca startu do wybranego miejsca końca, a także pozostawić tylko jedną granicę zakresu.",
          "W modalu tworzenia planu dodano podpisy do pól wyboru, żeby selekty były czytelne bez domyślnych placeholderów.",
        ],
      },
      {
        key: "fixes",
        title: "Poprawki",
        description: "Błędy i problemy usunięte w tej wersji.",
        accentColor: "blue.500",
        items: [
          "Udoskonalono listę planów, aby nie obcinała przycisków akcji po prawej stronie i lepiej mieściła kolumny w widoku.",
          "Modal blokady startu inwentaryzacji pokazuje teraz nazwę magazynu zamiast UUID oraz aktywne zadanie, które blokuje lokację.",
          "W komunikacie blokady można od razu otworzyć zadanie inwentaryzacji odpowiedzialne za zajętą lokację.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Pełne anulowanie zagnieżdżonych meldunków",
    releaseDate: "2026-06-07",
    summary:
      "Naprawiono ścieżkę cofania produkcji, aby można było bezpiecznie anulować cały łańcuch meldunków i przywrócić surowce na stan.",
    buckets: [
      {
        key: "fixes",
        title: "Poprawki",
        description: "Błędy i problemy usunięte w tej wersji.",
        accentColor: "blue.500",
        items: [
          "Usunięto błąd, przez który po anulowaniu meldunku produktu B system nadal blokował usunięcie wcześniejszego meldunku produktu A komunikatem o braku dostępnego stanu na tej samej lokalizacji.",
          "Podczas rollbacku zużycia wyprodukowanej partii system poprawnie odtwarza teraz agregat magazynowy dla lokalizacji, dzięki czemu pełna ścieżka anulowania zagnieżdżonej produkcji przywraca zarówno półprodukt, jak i surowiec.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Meldunki z wielu partii i czytelniejsza lista zleceń",
    releaseDate: "2026-06-06",
    summary:
      "Usprawniono rozliczanie materiałów w meldunkach produkcyjnych oraz poprawiono ergonomię listy zleceń produkcyjnych.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Meldunek produkcyjny pozwala teraz rozliczyć jeden materiał z wielu lokalizacji lub partii w ramach tej samej pozycji technologii.",
          "Lista zleceń produkcyjnych pokazuje polskie statusy, kolorowe oznaczenia stanu oraz krótki opis technologii pod numerem zlecenia.",
          "Filtr statusu na liście zleceń produkcyjnych zawiera teraz opcję `Wszyscy`.",
        ],
      },
      {
        key: "fixes",
        title: "Poprawki",
        description: "Błędy i problemy usunięte w tej wersji.",
        accentColor: "blue.500",
        items: [
          "Usunięto błąd, przez który meldunek odrzucał materiał wymagający pobrania z dwóch różnych partii, mimo że łączny dostępny stan był wystarczający.",
          "Poprawiono układ tabeli listy zleceń produkcyjnych, aby badge statusu nie nachodził już na numer zlecenia.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Technologie i drobne zużycie materiałów",
    releaseDate: "2026-06-06",
    summary:
      "Rozszerzono zarządzanie technologiami oraz poprawiono rozliczanie meldunków produkcyjnych dla materiałów zużywanych w ułamkowych ilościach.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Lista technologii obsługuje teraz filtrowanie po statusie: aktywne, nieaktywne, archiwalne i wszystkie.",
          "Technologia używana już w zleceniach produkcyjnych jest przy usuwaniu archiwizowana zamiast bezpowrotnie kasowana, co pozwala zachować historię operacyjną.",
          "Na karcie technologii dodano czytelniejsze rozróżnienie między usunięciem a archiwizacją oraz blokadę kasowania aktywnej technologii przed jej wcześniejszym wyłączeniem.",
        ],
      },
      {
        key: "fixes",
        title: "Poprawki",
        description: "Błędy i problemy usunięte w tej wersji.",
        accentColor: "blue.500",
        items: [
          "Usunięto błąd, przez który meldunek odrzucał technologię zużywającą ułamkową ilość materiału wejściowego, np. `0.125` opakowania kleju na jedno wykonanie.",
          "System pozwala teraz rozliczać częściowe zużycie materiałów wejściowych w stanach magazynowych i warstwach kosztowych, zachowując blokadę ułamkowych ilości dla produktów wyjściowych liczonych w sztukach.",
          "Uporządkowano statusy technologii w API i UI, aby archiwalne rekordy były poprawnie ukrywane z list aktywnych, ale nadal dostępne do historii i filtrowania.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Rozliczenia technologii i podział partii",
    releaseDate: "2026-06-05",
    summary:
      "Wprowadzono rozliczanie technologii na podstawie liczby realizacji (run_count) oraz dodano obsługę częściowych przesunięć dla wyprodukowanych partii.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Meldunki produkcyjne są teraz rozliczane na podstawie 'liczby realizacji technologii' zamiast jednej głównej ilości wyprodukowanej. Pozwala to na bezbłędne rozliczanie technologii z wieloma produktami wyjściowymi.",
          "Zaktualizowano interfejs zleceń produkcyjnych, by czytelnie wyświetlał liczbę realizacji oraz sumaryczne ilości dla każdego z wyprodukowanych towarów.",
        ],
      },
      {
        key: "fixes",
        title: "Poprawki",
        description: "Błędy i problemy usunięte w tej wersji.",
        accentColor: "blue.500",
        items: [
          "Naprawiono błąd blokujący częściowe przesunięcia (MMW/MMP) dla wyprodukowanych partii. System teraz automatycznie rozszczepia partię produkcyjną podczas częściowego przesunięcia, zachowując pełną identyfikowalność.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Partie produkcyjne w meldunkach i przesunięciach",
    releaseDate: "2026-06-04",
    summary:
      "Usprawniono pracę z towarami wyprodukowanymi w systemie: partie produkcyjne są teraz poprawnie przenoszone, zużywane i wybierane w dokumentach operacyjnych.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Dodano obsługę `warehouse_lot_id` w pozycjach meldunku produkcyjnego, aby zużycie materiału mogło wskazywać konkretną wyprodukowaną partię.",
          "Dodano warstwę kosztową FIFO dla wyprodukowanych partii, dzięki czemu taki towar może być użyty jako materiał w kolejnym meldunku z zachowaniem kosztu.",
          "W modalnym wyborze lokalizacji materiału dla nowego meldunku lista jest od razu filtrowana do wymaganego produktu z technologii.",
        ],
      },
      {
        key: "fixes",
        title: "Poprawki",
        description: "Błędy i problemy usunięte w tej wersji.",
        accentColor: "blue.500",
        items: [
          "Poprawiono tworzenie meldunku dla materiałów będących wyprodukowanymi partiami, gdy stan jest widoczny w `warehouse_lots`, ale agregat magazynowy nie jest zsynchronizowany.",
          "Poprawiono przesunięcia `MMW/MMP` dla wyprodukowanych partii, tak aby partia przechodziła na magazyn docelowy zamiast pozostawać tylko w saldzie zbiorczym.",
          "Zablokowano niebezpieczne częściowe przyjęcie lub odrzucenie przesunięcia wyprodukowanej partii, dopóki system nie obsługuje bezpiecznego podziału partii.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Ruchy partii i metadata",
    releaseDate: "2026-05-12",
    summary:
      "Rozszerzono WMS o obsługę ruchów na poziomie partii oraz dokładniejsze metadane i komunikaty operacyjne.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Dodano obsługę ruchów magazynowych z uwzględnieniem konkretnych partii.",
          "Rozszerzono zapis metadanych dokumentów i operacji magazynowych.",
        ],
      },
      {
        key: "fixes",
        title: "Poprawki",
        description: "Błędy i problemy usunięte w tej wersji.",
        accentColor: "blue.500",
        items: [
          "Doprecyzowano błędy i lokalizację komunikatów związanych z ruchem towaru.",
        ],
      },
    ],
  },
  {
    releaseLabel: "GUS i dopracowanie ofert sprzedaży",
    releaseDate: "2026-05-09",
    summary:
      "Dodano integrację GUS w backendzie i frontendzie oraz usprawniono pracę z ofertami sprzedaży.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Dodano pobieranie i podpowiadanie danych firm z GUS.",
          "Dodano endpointy statusowe dla integracji GUS i widoczność ich stanu w UI.",
          "Usprawniono edycję cen, ilości, rabatów i VAT w ofertach sprzedaży.",
        ],
      },
      {
        key: "fixes",
        title: "Poprawki",
        description: "Błędy i problemy usunięte w tej wersji.",
        accentColor: "blue.500",
        items: [
          "Uporządkowano zależności i rejestrację publicznych tras GUS w module licencyjnym.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Ergonomia formularzy i wersjonowanie aplikacji",
    releaseDate: "2026-05-06",
    summary:
      "Duża aktualizacja ergonomii pracy operatora: prostsze pickery, czytelniejsze formularze i widoczne dane wersji systemu.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Dodano widoczne metadane wersji frontend/backend wraz z tagiem, SHA i czasem builda.",
          "Kliknięcie w pola wyboru produktu otwiera picker bez potrzeby używania osobnych akcji.",
          "Usprawniono tabelę ofert sprzedaży oraz pracę z kontrahentami filtrowanymi po roli.",
        ],
      },
      {
        key: "fixes",
        title: "Poprawki",
        description: "Błędy i problemy usunięte w tej wersji.",
        accentColor: "blue.500",
        items: [
          "Rozróżniono wynik usuwania produktu na archiwizację i trwałe usunięcie.",
          "Uproszczono formularz produktów, usuwając ręczny toggle usługi i porządkując pola wspólne.",
          "Zaktualizowano dane kontaktowe w profilu dewelopera.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Oferta sprzedaży, eksport klienta i degradacja licencji",
    releaseDate: "2026-05-01",
    summary:
      "Wdrożono dokument `OS`, eksport danych klienta oraz politykę degradacji licencji przy błędach synchronizacji.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Dodano dokument `OS` z backendem, API, formularzami i routingiem.",
          "Dodano pricing modal i drukowanie ofert sprzedaży z cenami.",
          "Dodano eksport danych klienta i obsługę odzyskiwania po problemach z synchronizacją licencji.",
        ],
      },
      {
        key: "fixes",
        title: "Poprawki",
        description: "Błędy i problemy usunięte w tej wersji.",
        accentColor: "blue.500",
        items: [
          "Usprawniono konwersję `OS -> ZS/WZ` z podziałem po lokalizacjach i redukcją ilości do dostępnego stanu.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Inwentaryzacja i rebranding na Stockerra",
    releaseDate: "2026-04-28",
    summary:
      "Platforma otrzymała moduł inwentaryzacji, skanowanie kodów oraz rebranding na Stockerra.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Dodano wieloetapowy moduł inwentaryzacji z obsługą licencji i skanowaniem barcode przez kamerę.",
          "Rozszerzono funkcje drukowania i przygotowania dokumentów magazynowych.",
          "Przeprowadzono rebranding WMS do nazwy Stockerra.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Runtime licencji i mobilna obsługa LMS",
    releaseDate: "2026-04-19",
    summary:
      "Zbudowano nową logikę runtime licencji, obsługę mobile licensing i narzędzia operacyjne dla LMS.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Dodano runtime JWT issuance, refresh i zarządzanie sekretami instalacji.",
          "Dodano mobilne artefakty licencji oraz alerty i countdowny po stronie aplikacji mobilnej.",
          "Dodano konfigurację interwałów synchronizacji i obsługę kluczy publicznych.",
        ],
      },
      {
        key: "security",
        title: "Bezpieczeństwo",
        description:
          "Zmiany wzmacniające ochronę danych i stabilność środowiska.",
        accentColor: "orange.500",
        items: [
          "Uporządkowano logikę runtime token refresh i public key rotation.",
          "Dodano audyt bezpieczeństwa i czyszczenie nadmiarowych logów przy testach tokenów.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Przebudowa dokumentów i załączników WMS",
    releaseDate: "2026-04-14",
    summary:
      "Duży etap przebudowy dokumentów magazynowych: `ZS`, `WZ`, `PZ`, `PW`, `RW`, `MMW`, `ZM` oraz pliki do przyjęć.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Rozbudowano i wielokrotnie dopracowano dokumenty `ZS`, `WZ`, `PZ`, `PW`, `RW`, `MMW` i `ZM`.",
          "Dodano podgląd plików i zarządzanie załącznikami dla przyjęć zewnętrznych, `ZZ` i `ZAP`.",
          "Dodano współdzielone tabele i komponenty dokumentowe dla bardziej spójnego UI.",
        ],
      },
      {
        key: "fixes",
        title: "Poprawki",
        description: "Błędy i problemy usunięte w tej wersji.",
        accentColor: "blue.500",
        items: [
          "Poprawiono zaokrąglenia i liczby w pozycjach przyjęć oraz koszty dokumentów.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Kontrahenci, PDF-y i snapshoty dokumentów",
    releaseDate: "2026-03-28",
    summary:
      "Rozwinięto moduł kontrahentów, wprowadzono backendowe PDF-y oraz niezmienne snapshoty dokumentów.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Dodano moduł kontrahentów z tworzeniem, przywracaniem, wyszukiwaniem i postal code lookup.",
          "Wprowadzono backendowy serwis PDF do generowania spójnych dokumentów biznesowych.",
          "Dodano immutable snapshot model i backfill snapshotów dla starszych dokumentów.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Wyszukiwanie, lokalizacje i kontrola stanów",
    releaseDate: "2026-03-12",
    summary:
      "WMS dostał lepsze wyszukiwanie, szablony lokalizacji, historię lokalizacji i dynamiczne progi stanów minimalnych.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Dodano universal search dla magazynów, paczek i produktów.",
          "Dodano location templates i integrację szablonów lokalizacji w modułach WMS.",
          "Dodano historię lokalizacji produktów i dynamiczne zarządzanie minimalnym stanem `minQty`.",
          "Dodano widoczność stanu produktów między magazynami i backend-driven search.",
        ],
      },
    ],
  },
  {
    releaseLabel: "Fundament systemu i pierwsze moduły",
    releaseDate: "2026-02-18",
    summary:
      "Pierwszy etap budowy platformy: fundament techniczny, pliki produktów, uproszczone etykiety, nowe ekrany i porządkowanie środowiska.",
    buckets: [
      {
        key: "updates",
        title: "Nowe możliwości",
        description: "Nowe funkcje i ulepszenia dostępne po aktualizacji.",
        accentColor: "green.500",
        items: [
          "Dodano zarządzanie plikami produktów w backendzie i frontendzie.",
          "Dodano osobne strony dla zleceń produkcyjnych i technologii zamiast formularzy modalnych.",
          "Dodano uproszczone drukowanie etykiet oraz nowe ekrany magazynów i szczegółów produktów.",
          "Dodano i18n, konfigurację danych firmy i porządkowanie ścieżek wdrożeniowych WMS.",
        ],
      },
      {
        key: "security",
        title: "Bezpieczeństwo",
        description:
          "Zmiany wzmacniające ochronę danych i stabilność środowiska.",
        accentColor: "orange.500",
        items: [
          "Zaktualizowano zależności React i Go oraz uporządkowano podstawy środowiska build/deploy.",
        ],
      },
    ],
  },
]
